// Google Analytics 4 integration
// Loads gtag.js dynamically and provides typed event tracking functions

import { debug } from '@/utils/debug'
import { VERSION, GIT_HASH } from '@/version'

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

// Default measurement ID for production
const DEFAULT_MEASUREMENT_ID = 'G-XF3EZC0QT7'

const GA4_MEASUREMENT_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) || DEFAULT_MEASUREMENT_ID

let initialized = false

/**
 * Initialize GA4 by loading gtag.js dynamically
 * Safe to call multiple times - will only initialize once
 */
export function initGA4(): void {
  if (initialized) return

  if (!GA4_MEASUREMENT_ID) {
    debug.log('GA4', 'No measurement ID configured, skipping initialization')
    return
  }

  try {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || []
    // Must use function() and arguments (not arrow/rest params) for gtag to work
    window.gtag = function() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
    window.gtag('js', new Date())
    window.gtag('config', GA4_MEASUREMENT_ID, {
      app_version: VERSION,
      git_hash: GIT_HASH
    })

    // Load gtag.js script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`
    document.head.appendChild(script)

    initialized = true
    debug.success('GA4', `Initialized with measurement ID: ${GA4_MEASUREMENT_ID}`)
  } catch (error) {
    // Silently fail if blocked (e.g., by ad blocker)
    debug.warn('GA4', 'Failed to initialize (possibly blocked)', error)
  }
}

/**
 * Track a custom event
 * Safe to call even if GA4 is not initialized or blocked
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  try {
    if (window.gtag) {
      window.gtag('event', name, params)
      debug.log('GA4', `Event: ${name}`, params)
    }
  } catch {
    // Silently fail if blocked
  }
}

/**
 * Set the GA4 user ID for cross-session tracking
 * This enables GA4's built-in user identification feature
 */
export function setUserId(userId: string): void {
  try {
    if (window.gtag) {
      window.gtag('set', { user_id: userId })
      debug.log('GA4', `User ID set: ${userId}`)
    }
  } catch {
    // Silently fail if blocked
  }
}

// ============================================================================
// Typed event helpers
// ============================================================================

/**
 * Track session start when viewer initializes
 */
export function trackSessionStart(userAuthenticated: boolean): void {
  trackEvent('session_start', {
    user_authenticated: userAuthenticated,
    referrer: document.referrer || 'direct'
  })
}

export interface GardenLoadedOptions {
  gardenId: string
  objectCount: number
  loadTimeMs: number
  userId?: string
  demoMode?: boolean
  editMode?: boolean
  debugMode?: boolean
}

/**
 * Track when a garden finishes loading
 * Also sets the GA4 user ID if provided
 */
export function trackGardenLoaded(options: GardenLoadedOptions): void {
  const { gardenId, objectCount, loadTimeMs, userId, demoMode, editMode, debugMode } = options

  // Set user ID for GA4's built-in user tracking
  if (userId) {
    setUserId(userId)
  }

  trackEvent('garden_loaded', {
    garden_id: gardenId,
    object_count: objectCount,
    load_time_ms: loadTimeMs,
    user_id: userId,
    demo_mode: demoMode ?? false,
    edit_mode: editMode ?? false,
    debug_mode: debugMode ?? false
  })
}

/**
 * Track garden loading errors
 */
export function trackGardenError(errorType: string, errorCode?: string, message?: string): void {
  trackEvent('garden_error', {
    error_type: errorType,
    error_code: errorCode,
    error_message: message
  })
}

/**
 * Track camera mode changes (2D/3D)
 */
export function trackCameraModeChanged(mode: '2d' | '3d'): void {
  trackEvent('camera_mode_changed', {
    mode
  })
}

/**
 * Track walk mode toggle
 */
export function trackWalkModeToggled(enabled: boolean): void {
  trackEvent('walk_mode_toggled', {
    enabled
  })
}

/**
 * Track layer visibility changes
 */
export function trackLayerVisibilityChanged(layerName: string, visible: boolean): void {
  trackEvent('layer_visibility_changed', {
    layer_name: layerName,
    visible
  })
}
